<script setup>
import { computed, watch, ref } from 'vue'
import FilterBadge from '@/components/Global/FilterBadge.vue'
import HRDiagramOutput from '@/components/Analysis/HRDiagramOutput.vue'
import LightCurvePlot from '@/components/Analysis/LightCurvePlot.vue'
import PeriodogramPlot from '@/components/Analysis/PeriodogramPlot.vue'
import PhasedLightCurvePlot from '@/components/Analysis/PhasedLightCurvePlot.vue'

const props = defineProps({
  data: {
    type: Object,
    required: true,
    default: null,
  }
})

const emit = defineEmits(['closeAnalysisDialog'])

const periodogramData = ref({})
const variableStarData = ref({})
const bestPeriod = ref(null)
const selectedPoints = ref([])
// each output type has its own detection key, dispatching to the right set of plots
const hasCMD = computed(() => Array.isArray(props.data?.cmd))
const hasLightCurve = computed(() => Array.isArray(props.data?.light_curve) && props.data.light_curve.length > 0)
const hasPeriodogram = computed(() => {
  return !('aperture_radius' in props.data) && props.data.frequency?.length > 0 && props.data.power?.length > 0
})

function foldPeriod(magTimeSeries, period) {
  const frequency = 1.0 / period

  for (let i = 0; i < magTimeSeries.length; i++) {
    const mts = magTimeSeries[i]
    mts.phase = (mts.julian_date % period) * frequency
  }
}

function assignVariableStarData() {
  const data = props.data
  const magnitudeTimeSeries = data.light_curve || []
  const period = data.period
  const frequency = data.frequency
  const power = data.power

  if (magnitudeTimeSeries.length > 0) {
    variableStarData.value = {
      targetCoords: data.target_coords,
      magPhasedLightCurve: [],
      period: period,
      falseAlarmProbability: data.fap,
      fluxFallback: data.flux_fallback,
      excludedImages: data.excluded_images || [],
      source: data.operationInputData?.source || data.source,
      targetPositions: data.operationInputData?.target_positions,
      aperture: {
        apertureRadius: data.aperture_radius || data.operationInputData?.aperture_radius,
        annulusInnerRadius: data.annulus_inner_radius || data.operationInputData?.annulus_inner_radius,
        annulusOuterRadius: data.annulus_outer_radius || data.operationInputData?.annulus_outer_radius,
      },
      magnitudeTimeSeries: magnitudeTimeSeries,
    }
  } else {
    variableStarData.value = {}
  }

  // Pairing frequency and power data
  if (frequency && power && frequency.length === power.length) {
    const pairs = frequency.map((f, i) => ({ f: Number(f), p: Number(power[i]) }))
    const freqs = pairs.map(x => x.f)
    const pows = pairs.map(x => x.p)
    const periods = freqs.map(f => 1.0 / f)
    let peakIndex = 0
    let peakPower = pows[0] || 0
    // Finding peak power
    for (let i = 1; i < pows.length; i++) {
      if (pows[i] > peakPower) {
        peakPower = pows[i]
        peakIndex = i
      }
    }
    periodogramData.value = {
      frequencies: freqs,
      power: pows,
      periods: periods,
      peakIndex: peakIndex,
      peakFrequency: freqs[peakIndex],
      peakPeriod: periods[peakIndex],
      peakPower: peakPower
    }
  } else {
    periodogramData.value = {
      frequencies: [],
      power: [],
      periods: [],
      peakIndex: null,
      peakFrequency: null,
      peakPeriod: null,
      peakPower: null
    }
  }
  if (periodogramData.value.peakPeriod && variableStarData.value.magnitudeTimeSeries) {
    foldPeriod(variableStarData.value.magnitudeTimeSeries, periodogramData.value.peakPeriod)
    variableStarData.value.period = periodogramData.value.peakPeriod
    variableStarData.value.magPhasedLightCurve = [...variableStarData.value.magnitudeTimeSeries].sort((a, b) => a.phase - b.phase)
  }
}

function applySelectedPeriod(period) {
  foldPeriod(props.data.light_curve, period)
  // store the selected period and update phased LC
  variableStarData.value.period = period
  variableStarData.value.magPhasedLightCurve = [...props.data.light_curve].sort((a, b) => a.phase - b.phase)
}

const handlePeriodSelected = (period, freq, pow, bestPeriodFromPlot) => {
  applySelectedPeriod(period)
  bestPeriod.value = bestPeriodFromPlot
  selectedPoints.value = [{ x: freq, y: pow }]
}

watch(() => props.data, () => {
  if (hasLightCurve.value || hasPeriodogram.value) {
    assignVariableStarData()
  }
},
{ immediate: true }
)

const title = computed(() => {
  let text = props.data?.operationName || 'Unknown'
  if (props.data?.source) {
    text += ': ' + props.data.source?.name
  }
  else if (props.data?.cluster?.name) {
    text += ': ' + props.data.cluster.name
  }
  return text
})

</script>
<template>
  <v-sheet class="analysis-page">
    <v-toolbar
      class="analysis-toolbar"
      density="comfortable"
    >
      <filter-badge
        v-if="props.data.filter"
        :filter="props.data.filter"
        class="ml-2"
      />
      <template v-if="hasCMD">
        <filter-badge
          v-if="props.data.blue_filter"
          :filter="props.data.blue_filter"
          class="ml-2"
        />
        <filter-badge
          v-if="props.data.red_filter"
          :filter="props.data.red_filter"
          class="ml-1"
        />
      </template>
      <v-toolbar-title :text="title" />
      <v-btn
        icon="mdi-close"
        color="var(--cancel)"
        @click="emit('closeAnalysisDialog')"
      />
    </v-toolbar>
    <template v-if="props.data">
      <h-r-diagram-output
        v-if="hasCMD"
        :data="props.data"
      />
      <div
        v-else
        class="analysis-content"
      >
        <div
          v-if="hasLightCurve"
          class="analysis-row"
        >
          <light-curve-plot
            :variable-star-data="variableStarData"
            class="light-curve-plot"
          />
        </div>
        <div
          v-if="hasPeriodogram"
          class="analysis-row"
        >
          <periodogram-plot
            :periodogram-data="periodogramData"
            :variable-star-data="variableStarData"
            class="periodogram-plot"
            @period-selected="handlePeriodSelected"
          />
        </div>
        <div
          v-if="hasPeriodogram"
          class="analysis-row phased-analysis-row"
        >
          <phased-light-curve-plot
            :variable-star-data="variableStarData"
            :periodogram-data="periodogramData"
            :best-period="bestPeriod"
            :selected-points="selectedPoints"
            class="period-plot"
          />
        </div>
      </div>
    </template>
  </v-sheet>
</template>
<style scoped>
.analysis-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 2rem clamp(1rem, 4vw, 4rem) 3rem;
  min-height: 0;
}
.analysis-page{
  background-color: var(--primary-background);
  color: greenyellow;
  font-family: var(--font-stack);
  height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.analysis-toolbar{
  color: var(--text);
  background-color: var(--header);
}
.analysis-row {
  flex: 0 0 auto;
  height: clamp(360px, 46vh, 560px);
  width: 100%;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.phased-analysis-row {
  height: clamp(420px, 54vh, 640px);
}
</style>
